import { Box, BoxProps, Checkbox, HStack, Stack, Text } from "@chakra-ui/react";
import { observer } from "mobx-react";
import { getOptionTranslation, NumberOfLanesType } from "@keita/lanepatrol-sdk";
import { generateCraOptions, SelectOption } from "./utils";
import { ReactNode } from "react";
import { VerboseSelectOptions } from "./VerboseSelectOptions";
import { PresenceDistance } from "../../../models/HlaLane";
import { t } from "i18next";
import { defaultStrokeColor } from "../../../../map/utils/map-utils";
import { CreateOrUpdateHlaLane } from "../../../models/HlaLane";
import { CreateOrUpdateSimulationLane } from "../../../models/SimulationLane";
import { UpdateHlaWay } from "../../../models/HlaWay";

type Side = "left" | "right";

const AdjacentFeaturesFormBase = ({
  canEdit,
  lane,
  code,
}: {
  canEdit: boolean;
  lane: CreateOrUpdateHlaLane | CreateOrUpdateSimulationLane;
  code: (updater: (prev: UpdateHlaWay) => UpdateHlaWay) => void;
}) => {
  const directionkey = lane.frontFacing ? "frontFacingLane" : "backFacingLane";

  const left = lane.adjacentFeaturesLeft;
  const right = lane.adjacentFeaturesRight;

  const bySide = (side: Side) => (side === "left" ? left : right);

  const setValue = (side: Side, name: string, value: any) => {
    const sideKey =
      side === "left" ? "adjacentFeaturesLeft" : "adjacentFeaturesRight";

    code((prev) => {
      prev[directionkey]![sideKey] = {
        ...prev[directionkey]![sideKey],
        [name]: value,
      };
      return { ...prev };
    });
  };

  const setAdjacentFeaturesDependencies = (
    side: Side,
    name: string,
    value: PresenceDistance | null,
  ) => {
    const adjacentFeatures = bySide(side);

    if (value) {
      if (value === PresenceDistance.From0to1m) {
        if (
          name !== "roadLane" &&
          adjacentFeatures.roadLane === PresenceDistance.From0to1m
        ) {
          setValue(side, "roadLane", null);
        }
        if (
          name !== "vehicleParking" &&
          adjacentFeatures.vehicleParking === PresenceDistance.From0to1m
        ) {
          setValue(side, "vehicleParking", null);
        }
        if (
          name !== "sidewalk" &&
          adjacentFeatures.sidewalk === PresenceDistance.From0to1m
        ) {
          setValue(side, "sidewalk", null);
        }
      }
      if (value === PresenceDistance.From1to3m) {
        if (
          name !== "roadLane" &&
          adjacentFeatures.roadLane === PresenceDistance.From1to3m
        ) {
          setValue(side, "roadLane", null);
        }
        if (
          name !== "vehicleParking" &&
          adjacentFeatures.vehicleParking === PresenceDistance.From1to3m
        ) {
          setValue(side, "vehicleParking", null);
        }
        if (
          name !== "sidewalk" &&
          adjacentFeatures.sidewalk === PresenceDistance.From1to3m
        ) {
          setValue(side, "sidewalk", null);
        }
      }
    }
  };

  return (
    <Stack flexBasis={1} flexGrow={1}>
      <Field
        label={t(`shared:hlaAttribute.hardSegregation`)}
        colorCircle={
          <ColorCircle
            bgImage={"linear-gradient(45deg, #ffffff 50%, #d52424 50%)"}
            bgSize={"10px 10px"}
          />
        }
      >
        {(side) => (
          <Checkbox.Root
            disabled={!canEdit}
            cursor={!canEdit ? "auto" : "pointer"}
            colorPalette="primary"
            checked={bySide(side).hardSegregation}
            onCheckedChange={({ checked }) => {
              setValue(side, "hardSegregation", checked);
            }}
          >
            <Checkbox.HiddenInput />
            <Checkbox.Control />
          </Checkbox.Root>
        )}
      </Field>

      <Field
        label="Road lane"
        colorCircle={
          <ColorCircle
            bgColor={"#e5e5f7"}
            bgImage={`linear-gradient(90deg, ${defaultStrokeColor} 0% 40%, #ffffff 40% 60%, ${defaultStrokeColor} 60% 100%)`}
            bgSize={"10px 10px"}
          />
        }
      >
        {(side) => {
          const adjacentFeatures = bySide(side);
          const options = presenceDistanceOptions;
          return (
            <VerboseSelectOptions
              nullable
              reversed={side === "left"}
              disabled={!canEdit}
              value={adjacentFeatures.roadLane}
              setValue={(value) => {
                setValue(side, "roadLane", value);
                setAdjacentFeaturesDependencies(side, "roadLane", value);
              }}
              options={options}
            />
          );
        }}
      </Field>

      <Field label="Number of lanes">
        {(side) =>
          bySide(side).roadLane == null ? (
            <HStack h={7} alignItems={"center"}>
              <Text fontSize={"xs"}>N/A</Text>
            </HStack>
          ) : (
            <VerboseSelectOptions
              reversed={side === "left"}
              disabled={!canEdit}
              value={bySide(side).numberOfLanes}
              setValue={(value) => {
                setValue(side, "numberOfLanes", value);
              }}
              options={generateCraOptions(Object.values(NumberOfLanesType))}
            />
          )
        }
      </Field>

      <Field
        label="Vehicle parking"
        colorCircle={
          <ColorCircle
            bgImage={`linear-gradient(0deg, ${defaultStrokeColor} 0% 40%, #ffffff 40% 60%, ${defaultStrokeColor} 60% 100%)`}
            bgSize={"10px 10px"}
          />
        }
      >
        {(side) => (
          <VerboseSelectOptions
            nullable
            reversed={side === "left"}
            disabled={!canEdit}
            value={bySide(side).vehicleParking}
            setValue={(value) => {
              setValue(side, "vehicleParking", value);
              setAdjacentFeaturesDependencies(side, "vehicleParking", value);
            }}
            options={presenceDistanceOptions}
          />
        )}
      </Field>

      <Field label="Sidewalk" colorCircle={<ColorCircle bgColor={"#bebebe"} />}>
        {(side) => (
          <VerboseSelectOptions
            nullable
            reversed={side === "left"}
            disabled={!canEdit}
            value={bySide(side).sidewalk}
            setValue={(value) => {
              setValue(side, "sidewalk", value);
              setAdjacentFeaturesDependencies(side, "sidewalk", value);
            }}
            options={presenceDistanceOptions}
          />
        )}
      </Field>

      <Field
        label="Object or level change"
        colorCircle={<ColorCircle bgColor={"#f5e761"} />}
      >
        {(side) => (
          <VerboseSelectOptions
            nullable
            reversed={side === "left"}
            disabled={!canEdit}
            value={bySide(side).objectOrLevelChange}
            setValue={(value) => {
              setValue(side, "objectOrLevelChange", value);
            }}
            options={presenceDistanceOptions}
          />
        )}
      </Field>

      <Field
        label="Severe hazard"
        colorCircle={<ColorCircle bgColor={"#ea5757"} />}
      >
        {(side) => (
          <VerboseSelectOptions
            nullable
            reversed={side === "left"}
            disabled={!canEdit}
            value={bySide(side).severeHazard}
            setValue={(value) => {
              setValue(side, "severeHazard", value);
            }}
            options={presenceDistanceOptions}
          />
        )}
      </Field>
    </Stack>
  );
};

const presenceDistanceOptions: SelectOption[] = [
  {
    value: PresenceDistance.From0to1m,
    translation: getOptionTranslation(PresenceDistance.From0to1m),
  },
  {
    value: PresenceDistance.From1to3m,
    translation: getOptionTranslation(PresenceDistance.From1to3m),
  },
];

const Field = ({
  label,
  children,
  colorCircle,
}: {
  label: string;
  colorCircle?: ReactNode;
  children: (side: Side) => ReactNode;
}) => {
  return (
    <HStack justifyContent={"center"}>
      <HStack justifyContent={"right"} flexBasis={1} flexGrow={1}>
        {children("left")}
      </HStack>

      <HStack justifyContent={"center"} flexShrink={0} w="200px">
        {colorCircle}
        <Text fontSize={"xs"} fontWeight={"semibold"}>
          {label}
        </Text>
      </HStack>

      <HStack justifyContent={"left"} flexBasis={1} flexGrow={1}>
        {children("right")}
      </HStack>
    </HStack>
  );
};

export const AdjacentFeaturesForm = observer(AdjacentFeaturesFormBase);

const ColorCircle = (props: BoxProps) => {
  return (
    <Box
      w={3}
      h={3}
      border={`1px solid ${defaultStrokeColor}`}
      background={"red"}
      borderRadius={8}
      {...props}
    />
  );
};
